import React, { useState, useMemo, useCallback, useRef } from 'react';
import { cloneDeep } from 'lodash';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { shouldShowContextMenu as calculateContextMenuVisibility } from 'utils/auto-tieout-element-utils';
import RolesUtil from 'permissions/utils/roles';
import { useDispatch, useSelector, useStore } from 'react-redux';
import Permissions from 'permissions/permissions';
import { useLocation, useParams } from 'react-router-dom';
import {
  isElementReportPreviewPage,
  getElementHighlightStatus,
  getCssModifierForView,
  getCalloutVisibilityDetails,
} from 'utils/ocr-annotation-utils';
import { getElementBoundaryInfo } from 'utils/ocr-element-utils';
import {
  AUTO_TIEOUT_COMPARE_MODE,
  ELEMENT_QUICK_ACTIONS,
  getQuickActionByElement,
  SIDE_BY_SIDE_COMPARE_MODE,
  SIDE_BY_SIDE_VIEW_RIGHT_BLOCK,
  STATEMENT_ELEMENT_ID_PREFIX,
  STATEMENT_LEFT_ELEMENT_ID_PREFIX,
  NO_TICKMARK_OPTION,
  NO_WORKPAPER_OPTION,
  ELLIPSIS_OPTION,
  LOADER,
  TRAIL_BALANCE_MODE,
  STATEMENT_TYPES,
} from 'constants/feature/statement-content-constants';
import { setSelectedElementDetails } from 'store/actions/report-preview-page-actions';
import { ROUTE_CONSTANTS } from 'constants/util/route-constants';
import {
  ELEMENT_HIGHLIGHT_STATES,
  ElementSelectionLimit,
} from 'constants/feature/tieout-element-constants';
import { onElementClick } from 'components/feature/statement-content-panel/tieout-element-component';
import ElementFlagComponentOCR from './element-flag-component-ocr';
import {
  setSourceStatementElementMapAction,
  setTargetStatementElementMapAction,
  setSourceStatementElementMapForAutoTieout,
  setTargetStatementElementMapForAutoTieout,
  setTargetStatementElementMapForTrialBalance,
} from 'store/actions/side-by-side-statement/side-by-side-statement-actions';
import {
  getExcessiveElementSelectionNotification,
  handleHighlightMappedElements,
  handleShowCrossIconByStmtWrappedUp,
  handleShowSelectionCursorForMappedElements,
} from 'constants/feature/tieout-element-utils';
import { isNullOrUndefined } from 'utils/object-utils';
import { updateSideBySideElementCacheByCallouts } from 'store/actions/statement-content-annotation-creation-actions';
import useHoverEffect from 'components/hooks/useHoverEffects';
import { FEATURE_NAMES } from 'constants/feature-flag-constants';
import { checkIfFeatureFlagEnabled } from 'store/actions/selected-statement-actions';
import { isModeForAddCursor } from 'utils/modes-utils';
import { setOcrCurrentViewPageNumber } from 'store/actions/ocr-current-view-page-number-actions';
import {
  handleElementFormulaHighlight,
  handleElementQuickAction,
  handleSystemFlagOverride,
  performManualMappingInAutoTieout,
  removeElementFormulaHighlight,
} from 'store/actions/statement-content-actions';
import {
  performManualMappingInTrialBalance,
  clearTrialBalanceRowHighlight,
} from 'store/actions/trial-balance/trial-balance-action';
import {
  clearSideBySideElementsMap,
  clearSelectedElementsContentPanel,
  clearBatchSelectedElementsAction,
} from 'store/actions/action-creators';
import { clearLeftSelectedElementsContentPanel } from 'store/actions/statement-content-actions';
import {
  unlinkElementWorkpaperFromAutoTieout,
  attachTickmarkToElement,
  getHistoryForElementPanel,
} from 'store/actions/element-panel-actions';
import { ReactComponent as CrossIcon } from 'icons/cross-icon.svg';
import Tooltip from 'components/common/tool-tip-component';
import { CROSS_ICON_SIZE } from 'constants/feature/tieout-element-constants';
import { handleAutoTieoutElementClick as autoTieoutElementClick } from 'utils/auto-tieout-element-utils';
import QuickActionElementContextMenu from './quick-action-element-context-menu';
import CreateTickmark from 'components/feature/statement-content-panel/quick-action/create-tickmarks';
import { ELEMENT_SELECT_MODES } from 'constants/feature/modes-constants';
import ConfirmOverrideSystemFlag from 'components/feature/element-panel/confirm-override-system-flag-modal-component';
import { createConfirmOverrideSystemFlagHandlers } from 'utils/confirm-override-system-flag-utils';
import { updateElementCacheByCalloutAction } from 'store/actions/element-cache-actions';
import CreateWorkPaper from './quick-action/create-workpaper';
import AttachExistingTickmarks from 'components/feature/statement-content-panel/quick-action/attach-existing-tickmarks';
import AttachExistingWorkpapers from 'components/feature/statement-content-panel/quick-action/attach-existing-workpapers';
import { getTickmarksForRevisionRequest } from 'api/tickmark-api';
import { getWorkpapersForRevisionRequest } from 'api/workpaper-api';
import { removeObjectsByOrdinal } from 'constants/feature/tieout-element-utils';
import {
  AUTO_TIEOUT_WORKPAPER_TYPE,
  AUTO_TIEOUT_TB_WORKPAPER_TYPE,
} from 'constants/feature/statement-creation-constants';
import WorkpaperForm from 'models/form/workpaper-form-model';
import { updateWorkpaper } from 'store/actions/workpaper-actions';
import {
  removeTargetElementClicked,
  removeSourceElementClicked,
} from 'store/actions/auto-tieout/mapped-elements-actions';
import {
  removeTBSourceElementClicked,
  setTBTargetElementClicked,
  removeTBTargetElementClicked,
} from 'store/actions/trial-balance/mapped-element-action';
import {
  INCOME_STATEMENT_WORKPAPER,
  trialBalanceWPIcon,
  VERIFIED_STATUS,
  WORKPAPER_TYPE,
} from 'constants/feature/element-panel-constants';
import { setElementToScrollInTrialBalanceView } from 'store/actions/trial-balance/trial-balance-action';
import ManageElementAttributesMenu from './quick-action/manage-element-attributes-menu';
import ConfirmationModal from 'components/common/confirmation-modal';

const TIEOUT_ELEMENT = 'tieout-element-ocr';
const SIDE_BY_SIDE_STATEMENT_ELEMENT = 'side-by-side-selected-element';
const SIDE_BY_SIDE_ELEMENT_IDENTIFIER = 'side-by-side-element-identifier';
const TIEOUT_ELEMENT_LEFT = 'tieout-element-ocr-left';

const TieoutElementOCR = ({
  clearSelection,
  elementDetails,
  polygonInPixel,
  showElementStatusAndFlag,
  annotationDisplayFilter,
  annotation,
  isLeftView,
  isDisabled,
  showAnnotationIcons,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const url = useLocation().pathname;
  const [contextMenu, setContextMenu] = useState(null);
  const [menuOptions, setMenuOptions] = useState([]);
  const [showTickmarkModal, setShowTickmarkModal] = useState(false);
  const [showWorkPaperModal, setShowWorkPaperModal] = useState(false);
  const [showConfirmOverrideSystemFlag, setShowConfirmOverrideSystemFlag] =
    useState(false);
  const [overrideSystemFlagElement, setOverrideSystemFlagElement] =
    useState(null);
  const [showTickmarkListModal, setShowTickmarkListModal] = useState(false);
  const [existingTickmarkList, setExistingTickmarkList] = useState([]);
  const [showWorkpaperListModal, setShowWorkpaperListModal] = useState(false);
  const [showReviewConfirmationModal, setShowReviewConfirmationModal] =
    useState(false);
  const [existingWorkpaperList, setExistingWorkpaperList] = useState([]);
  const [attachedWorkpaperIds, setAttachedWorkpaperIds] = useState([]);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [showManageAttributesMenu, setShowManageAttributesMenu] =
    useState(false);
  const [clickPosition, setClickPosition] = useState(null);

  const contextMenuIdRef = React.useRef(null);
  const elementRef = useRef(null);
  const menuIdRef = React.useRef(`menu-${elementDetails?.id}`);
  const contextMenuPositionRef = React.useRef(null);
  const { projectId, statementId, revisionId } = useParams();
  const isSideBySideView = url.includes(ROUTE_CONSTANTS.SIDE_BY_SIDE);
  const ui = useSelector((store) => store.ui);
  const data = useSelector((store) => store.data);
  const {
    sourceStatementParams,
    sideBySideView: {
      sideBySideElementMap,
      sideBySideViewMode: { viewMode },
      shouldHighlightMappedElements: { highlightMappedElements },
      autoTieout: {
        mappedElements: autoTieoutElementData,
        workFlowStatus,
        workpaperDetails,
      },
      trialBalance: {
        tbWorkFlowStatus,
        mappedElements: trialBalanceMappedElements,
        isSnapshotViewLoaded,
        isSnapshotReadOnly,
      },
    },
    reportPreviewPage,
    statementPage: {
      blacklineHighlightedElementId,
      selectedElementsMap,
      leftSelectedElementsMap,
      modes: { selectMode, blacklineViewMode, blacklineViewShowElementMode },
      leftSectionsInView,
      sectionsInView,
    },
  } = ui;

  const isSideBySideMode =
    isSideBySideView && viewMode === SIDE_BY_SIDE_COMPARE_MODE;
  const isAutoTieoutMode =
    isSideBySideView && viewMode === AUTO_TIEOUT_COMPARE_MODE;
  const isTrialBalanceMode =
    isSideBySideView && viewMode === TRAIL_BALANCE_MODE;

  const effectiveWorkFlowStatus = isTrialBalanceMode
    ? tbWorkFlowStatus
    : workFlowStatus;

  const TIEOUT_ELEMENT_CLASS = isLeftView
    ? TIEOUT_ELEMENT_LEFT
    : TIEOUT_ELEMENT;

  const TIEOUT_ELEMENT_ID_PREFIX = isLeftView
    ? STATEMENT_LEFT_ELEMENT_ID_PREFIX
    : STATEMENT_ELEMENT_ID_PREFIX;

  const {
    currentUser,
    batchPanel: { batchElementList },
    selectedProject: { id },
    statementSummary: {
      statementSummaryElementsDisplay: { displayElementDetailsOnStatementPage },
    },
    leftStatementContent: { leftSectionIdList },
    selectedStatement,
    leftSelectedStatement: { isOCR: leftStatementIsOCR },
    editPermission: { editPermissionAutotieout },
  } = data;

  const isCftEnabled = selectedStatement?.statement?.comfortLetter;

  const sourceElementClicked = autoTieoutElementData?.getSourceElementClicked();
  const targetElementClicked = autoTieoutElementData?.getTargetElementClicked();

  let autoTieoutElementToHighlight = sourceElementClicked
    ? sourceElementClicked
    : targetElementClicked;
  const tbSourceElementClicked =
    trialBalanceMappedElements?.getSourceElementClicked();
  const tbTargetElementClicked =
    trialBalanceMappedElements?.getTargetElementClicked();

  const trialBalanceElementToHighlight = tbSourceElementClicked
    ? tbSourceElementClicked
    : tbTargetElementClicked;
  const selectedStatementIsOCR = selectedStatement?.isOCR;

  const autoTieoutMappedElementDetails = isAutoTieoutMode
    ? autoTieoutElementData
    : trialBalanceMappedElements;

  const shouldHighlightMappedElements = () => {
    return handleHighlightMappedElements({
      isSideBySideView,
      highlightMappedElements,
      isAutoTieoutMode,
      isTrialBalanceMode,
      isLeftView,
      elementDetails,
      autoTieoutMappedElementDetails,
    });
  };

  const isStatementWrappedOrPendingApproval =
    selectedStatement?.isStatementWrappedOrPendingApproval();

  const isReadOnlyStatement = selectedStatement?.isReadOnly();
  const isReadOnlyOverride = selectedStatement?.data?.readOnlyOverride;

  const isObserverRole = RolesUtil.doesUserHaveObserverRoleForProject(
    data,
    projectId,
  );

  //side-by-side view
  const hasPermission =
    Permissions.SideBySideStatementViewPermissions.canSelectElement(data, id);

  const { handleMouseEnter, handleMouseLeave } = useHoverEffect(
    TIEOUT_ELEMENT_CLASS,
    elementDetails?.id,
  );

  React.useEffect(() => {
    const handleCloseOtherContextMenus = (event) => {
      if (event.detail.id !== contextMenuIdRef.current) {
        setContextMenu(null);
      }
    };

    const handleCloseAllMenus = (event) => {
      // Close this menu if the event is from a different element or context menu
      if (event.detail.id !== menuIdRef.current) {
        setShowManageAttributesMenu(false);
        setClickPosition(null);
      }
    };

    window.addEventListener(
      'closeOtherContextMenus',
      handleCloseOtherContextMenus,
    );
    window.addEventListener('closeAllAttributeMenus', handleCloseAllMenus);

    return () => {
      window.removeEventListener(
        'closeOtherContextMenus',
        handleCloseOtherContextMenus,
      );
      window.removeEventListener('closeAllAttributeMenus', handleCloseAllMenus);
    };
  }, []);

  // Close ManageElementAttributesMenu when context menu opens
  React.useEffect(() => {
    if (contextMenu) {
      setShowManageAttributesMenu(false);
      setClickPosition(null);
    }
  }, [contextMenu]);

  //side-by-side view element selection count
  const numberOfSourceElementSelected =
    sideBySideElementMap && sideBySideElementMap.sizeOfSourceMapping;
  const numberOfTargetElementSelected =
    sideBySideElementMap && sideBySideElementMap.sizeOfTargetMapping;

  const isPreviewPage = isElementReportPreviewPage(url);
  const isElementSelected = useMemo(() => {
    if (isPreviewPage) {
      return (
        reportPreviewPage.selectedElementId !== null &&
        elementDetails.id === reportPreviewPage.selectedElementId &&
        !reportPreviewPage.isClickedOnReport
      );
    } else if (blacklineViewMode) {
      const { addedOrChanged } = blacklineHighlightedElementId;
      if (addedOrChanged) {
        return (
          !isLeftView &&
          blacklineViewShowElementMode &&
          elementDetails.id === addedOrChanged
        );
      }

      return false;
    } else if (isLeftView) {
      return leftSelectedElementsMap.has(elementDetails.id);
    } else {
      return selectedElementsMap.has(elementDetails.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    blacklineViewMode,
    blacklineViewShowElementMode,
    elementDetails.id,
    isLeftView,
    isPreviewPage,
    leftSelectedElementsMap,
    reportPreviewPage.isClickedOnReport,
    reportPreviewPage.selectedElementId,
    blacklineHighlightedElementId.deleted,
    blacklineHighlightedElementId.addedOrChanged,
    selectedElementsMap,
  ]);

  //side-by-side view
  const _shouldEnableTargetElementForSelection = (elementId) => {
    if (isSideBySideView && !isLeftView && sideBySideElementMap) {
      return (
        sideBySideElementMap.mapKeyOfFirstUnmappedTargetElement ||
        sideBySideElementMap.getTargetElementMapByElementId(elementId)
      );
    }
  };

  //side-by-side view element disable logic
  const isSideBySideViewDisabled = (elementId) => {
    if (isSideBySideView) {
      if (isSideBySideMode) {
        return isLeftView
          ? !hasPermission
          : !(
              hasPermission && _shouldEnableTargetElementForSelection(elementId)
            );
      } else if (isAutoTieoutMode) {
        if (!isLeftView && isModeForAddCursor(selectMode)) {
          return false;
        }
        return !leftSectionIdList.isLoaded;
      } else if (isTrialBalanceMode) {
        if (isLeftView) {
          return true;
        }
        return false;
      } else {
        return true;
      }
    }
  };

  const isElementHighlighted = useMemo(() => {
    return isPreviewPage
      ? reportPreviewPage.selectedElementId === elementDetails.id &&
          reportPreviewPage.isClickedOnReport
      : false;
  }, [
    elementDetails.id,
    isPreviewPage,
    reportPreviewPage.isClickedOnReport,
    reportPreviewPage.selectedElementId,
  ]);

  const elementHighlightState = () => {
    return isLeftView
      ? leftSelectedElementsMap.getElementHighlightColor(elementDetails.id)
      : selectedElementsMap.getElementHighlightColor(elementDetails.id);
  };

  const { polygonWidth, polygonHeight, polygonLeft, polygonTop } =
    getElementBoundaryInfo(polygonInPixel);

  const styles = {
    cursor:
      (!isSideBySideView ||
        ((isTrialBalanceMode || isAutoTieoutMode) && !isLeftView)) &&
      isModeForAddCursor(selectMode)
        ? 'alias'
        : null,
    top: polygonTop,
    left: polygonLeft,
    width: polygonWidth,
    height: polygonHeight,
  };

  //side-by-side view
  const getCountForSelectedElementForSideBySideMapping = (elementId) => {
    if (isSideBySideMode || isAutoTieoutMode || isTrialBalanceMode) {
      if (isLeftView) {
        return (
          sideBySideElementMap &&
          sideBySideElementMap.getCountForMappedSourceElement(elementId)
        );
      }
      return (
        sideBySideElementMap &&
        sideBySideElementMap.getCountForMappedTargetElement(elementId)
      );
    } else return null;
  };

  const _countForSelectedElementForSideBySideMapping =
    getCountForSelectedElementForSideBySideMapping(elementDetails.id);

  //side-by-side view
  const getCountForSelectedElement = () => {
    const elementCount =
      sideBySideElementMap && sideBySideElementMap.getCountOfElementsSelected();
    return elementCount + 1;
  };

  //Hiding unlink WP icon for Wrapped up statements or snapshot read only or observer role
  const handleCrossIconByStmtWrappedUpState = () => {
    return handleShowCrossIconByStmtWrappedUp({
      isStatementWrappedOrPendingApproval:
        isStatementWrappedOrPendingApproval ||
        isSnapshotReadOnly ||
        isObserverRole,
      isTrialBalanceMode,
      workFlowStatus: isAutoTieoutMode ? workFlowStatus : tbWorkFlowStatus,
      isAutoTieoutMode,
      leftView: isLeftView,
      mappedElements: isTrialBalanceMode
        ? trialBalanceMappedElements
        : autoTieoutElementData,
      elementDetails,
      isReadOnlyOverride, // If statement is previous revision, hide the unlink icon in auto tieout view.
      editPermissionAutotieout,
    });
  };

  //Handle cursor when unlink WP icon for Wrapped up statements or snapshot read only or observer role
  const handleCursorByStmtWrappedUpState = () => {
    return handleShowSelectionCursorForMappedElements({
      isStatementWrappedOrPendingApproval:
        isStatementWrappedOrPendingApproval ||
        isSnapshotReadOnly ||
        isReadOnlyOverride ||
        isObserverRole,
      isTrialBalanceMode,
      workFlowStatus: isAutoTieoutMode ? workFlowStatus : tbWorkFlowStatus,
      isAutoTieoutMode,
      leftView: isLeftView,
      mappedElements: isTrialBalanceMode
        ? trialBalanceMappedElements
        : autoTieoutElementData,
      elementDetails,
    });
  };

  const handleAutoTieoutElementClick = useCallback(
    ({ elementDetails }) => {
      const elementId = elementDetails.id;
      if (
        isStatementWrappedOrPendingApproval &&
        workFlowStatus.isCompleted &&
        !isLeftView &&
        !autoTieoutElementData.isElementExistsInTargetMap(elementDetails.id)
      ) {
        return;
      }

      dispatch(clearSelectedElementsContentPanel());
      dispatch(clearLeftSelectedElementsContentPanel());

      const isAutoTieoutMappingFound = isLeftView
        ? autoTieoutElementData.getTargetElementForSource(elementId)
        : autoTieoutElementData.getSourceElementForTarget(elementId);

      if (!isNullOrUndefined(isAutoTieoutMappingFound)) {
        dispatch(clearSideBySideElementsMap());
        autoTieoutElementClick({
          elementId,
          isLeftView,
          isAutoTieoutMode,
          autoTieoutElementData,
          sectionsInView,
          leftSectionsInView,
          selectedStatementIsOCR,
          leftStatementIsOCR,
          dispatch,
        });
      } else if (
        !isObserverRole &&
        !isReadOnlyOverride &&
        editPermissionAutotieout
      ) {
        dispatch(removeSourceElementClicked());
        dispatch(removeTargetElementClicked());

        const elementData = elementDetails && elementDetails.data;
        const targetStatementParams = { projectId, statementId, revisionId };
        if (
          elementData &&
          leftSectionIdList.isLoaded &&
          workFlowStatus.isCompleted
        ) {
          if (isLeftView) {
            dispatch(setSourceStatementElementMapForAutoTieout(elementData));
            dispatch(
              performManualMappingInAutoTieout({
                sideBySideElementMap: sideBySideElementMap,
                sourceStatementParams: sourceStatementParams,
                targetStatementParams,
              }),
            );
          } else if (!isLeftView) {
            dispatch(setTargetStatementElementMapForAutoTieout(elementData));
            dispatch(
              performManualMappingInAutoTieout({
                sideBySideElementMap: sideBySideElementMap,
                sourceStatementParams: sourceStatementParams,
                targetStatementParams,
              }),
            );
          }
        }
      }
    },
    [
      isAutoTieoutMode,
      isLeftView,
      autoTieoutElementData,
      sectionsInView,
      leftSectionsInView,
      leftStatementIsOCR,
      leftStatementIsOCR,
      sideBySideElementMap,
      dispatch,
      editPermissionAutotieout,
    ],
  );

  const handleTrialBalanceElementClick = useCallback(
    ({ elementDetails }) => {
      if (
        isStatementWrappedOrPendingApproval &&
        tbWorkFlowStatus?.isCompleted &&
        !isLeftView &&
        !trialBalanceMappedElements?.isElementExistsInTargetMap(
          elementDetails?.id,
        )
      ) {
        return;
      }
      if (!isSnapshotViewLoaded) {
        return;
      }
      const elementId = elementDetails?.id;
      dispatch(clearSelectedElementsContentPanel());
      dispatch(clearBatchSelectedElementsAction());
      dispatch(clearTrialBalanceRowHighlight());

      const existingMapping =
        trialBalanceMappedElements?.getTargetMapping?.(elementId);
      const isElementAlreadyMapped =
        !isNullOrUndefined(existingMapping) && existingMapping.active;

      if (isElementAlreadyMapped) {
        const targetElementClicked =
          trialBalanceMappedElements?.getTargetElementClicked?.();
        const elementClickedId = targetElementClicked?.elementId;

        if (
          !isNullOrUndefined(elementClickedId) &&
          elementId == elementClickedId
        ) {
          dispatch(removeTBSourceElementClicked());
          dispatch(removeTBTargetElementClicked());
          return;
        }

        dispatch(clearSideBySideElementsMap());
        dispatch(removeTBSourceElementClicked());
        dispatch(setTBTargetElementClicked(existingMapping));
        const elementRowId = elementId && existingMapping?.tbDataId;
        dispatch(
          setElementToScrollInTrialBalanceView({
            elementId,
            rowId: elementRowId,
            tbWorkPaperType:
              elementDetails?.getTrialBalanceWorkpaperType() ===
              INCOME_STATEMENT_WORKPAPER
                ? STATEMENT_TYPES.INCOME_STATEMENT
                : STATEMENT_TYPES.BALANCE_SHEET,
          }),
        );
      } else if (
        !isSnapshotReadOnly &&
        !isObserverRole &&
        !isReadOnlyOverride &&
        editPermissionAutotieout &&
        tbWorkFlowStatus?.isCompleted
      ) {
        dispatch(removeTBSourceElementClicked());
        dispatch(removeTBTargetElementClicked());

        const elementData = elementDetails && elementDetails.data;
        const statementParams = { projectId, statementId, revisionId };

        if (elementData) {
          dispatch(setTargetStatementElementMapForTrialBalance(elementData));

          dispatch(
            performManualMappingInTrialBalance({
              statementParams,
            }),
          );
        }
      }
    },
    [
      trialBalanceMappedElements,
      projectId,
      statementId,
      revisionId,
      dispatch,
      isSnapshotViewLoaded,
      isSnapshotReadOnly,
      isObserverRole,
      editPermissionAutotieout,
    ],
  );

  const elementClick = (elementDetails) => {
    if (elementDetails) {
      const { id, sectionId } = elementDetails;
      if (isPreviewPage) {
        dispatch(
          setSelectedElementDetails({
            selectedElementId: id,
            sectionId,
            isClickedOnReport: false,
          }),
        );
        if (reportPreviewPage?.selectedElementsSectionId !== sectionId) {
          dispatch(setOcrCurrentViewPageNumber(sectionId));
        }
      } else if (!isPreviewPage && !blacklineViewMode) {
        dispatch(
          onElementClick({ elementId: id, sectionId, isDisabled: false }),
        );
      }
    }
  };

  const onClick = (e, elementDetails) => {
    e.stopPropagation();
    clearSelection();
    if (isSideBySideView) {
      // Prevent batch operations for left side view in trial balance and auto tieout modes
      const isBatchMode =
        selectMode === ELEMENT_SELECT_MODES.BATCH ||
        selectMode === ELEMENT_SELECT_MODES.BATCH_WITH_BANNER;
      if (
        isLeftView &&
        (isTrialBalanceMode || isAutoTieoutMode) &&
        isBatchMode
      ) {
        return;
      }
      if (
        (isTrialBalanceMode || isAutoTieoutMode) &&
        isModeForAddCursor(selectMode)
      ) {
        const { id, sectionId } = elementDetails;
        dispatch(
          onElementClick({ elementId: id, sectionId, isDisabled: false }),
        );
      } else if (isSideBySideMode && hasPermission) {
        let elementData = elementDetails && elementDetails.data;
        //side-by-side view
        if (getCountForSelectedElement() <= ElementSelectionLimit) {
          if (isLeftView)
            dispatch(setSourceStatementElementMapAction(elementData));
          else
            _shouldEnableTargetElementForSelection(elementData.id) &&
              dispatch(setTargetStatementElementMapAction(elementData));
        } else {
          if (
            isLeftView &&
            sideBySideElementMap.getSelectedSourceElementIfExists(
              parseInt(elementData.id),
            ) // check if the element is already selected in left view
          ) {
            dispatch(setSourceStatementElementMapAction(elementData));
            return;
          } else if (
            !isLeftView &&
            sideBySideElementMap.getSelectedTargetElementIfExists(
              parseInt(elementData.id),
            ) // check if the element is already selected in right view
          ) {
            _shouldEnableTargetElementForSelection(elementData.id) &&
              dispatch(setTargetStatementElementMapAction(elementData));
            return;
          } else {
            getExcessiveElementSelectionNotification(ElementSelectionLimit);
          }
        }
      }
    } else {
      elementClick(elementDetails);
    }
  };

  const isElementSelectedForSideBySideMap = (elementId) => {
    if (isSideBySideView) {
      if (isLeftView) {
        return (
          sideBySideElementMap &&
          sideBySideElementMap.getSourceElementMapByElementId(elementId)
        );
      }
      return (
        sideBySideElementMap &&
        sideBySideElementMap.getTargetElementMapByElementId(elementId)
      );
    } else return false;
  };

  const { isReviewed, isVerified, isUnverified, isSelected, isHighlighted } =
    getElementHighlightStatus({
      elementDetails,
      isElementSelected,
      isElementHighlighted,
      isPreviewPage,
      showElementStatusAndFlag,
      reportPreviewPage,
      blacklineViewMode,
    });

  //side-by-side view
  const isCursorAlias = useMemo(() => {
    // Show cursor alias for right side view in trial balance / auto tieout modes when in batch mode
    if (
      !isLeftView &&
      (isTrialBalanceMode || isAutoTieoutMode) &&
      isModeForAddCursor(selectMode)
    ) {
      return true;
    }
    if (isSideBySideMode) {
      if (!isSideBySideView || !hasPermission) return false;

      if (isLeftView) {
        return true;
      } else {
        // Right view: show cursor alias if source > target OR this element is selected
        const canSelectMore =
          numberOfSourceElementSelected > numberOfTargetElementSelected;
        const isThisElementSelected =
          sideBySideElementMap?.getSelectedTargetElementIfExists(
            parseInt(elementDetails.id),
          );

        return canSelectMore || !!isThisElementSelected;
      }
    }
    return false;
  }, [
    numberOfSourceElementSelected,
    numberOfTargetElementSelected,
    isLeftView,
    isSideBySideView,
    hasPermission,
    elementDetails.id,
    sideBySideElementMap,
    isSideBySideMode,
    isTrialBalanceMode,
    isAutoTieoutMode,
    selectMode,
  ]);

  const modifier = getCssModifierForView(
    url,
    blacklineViewMode,
    blacklineViewShowElementMode,
  );

  const shouldShowAnnotationsByMode =
    isLeftView && (isAutoTieoutMode || isTrialBalanceMode);

  const handleIndicatorOnHover = (elementId, showCallout) => {
    if (
      !annotationDisplayFilter.showAll &&
      elementDetails.isElementHasAnnotation(elementDetails.data)
    ) {
      const initialCalloutVisibilityDetails = {
        displayMarker: !annotation['marker'],
        displayCallout: shouldShowAnnotationsByMode || !annotation['callout'],
        displayAnnotation: !annotation['display'],
      };

      const { displayCallout, displayAnnotation } = getCalloutVisibilityDetails(
        initialCalloutVisibilityDetails,
        annotation.annotationsConfig,
        annotationDisplayFilter,
        shouldShowAnnotationsByMode,
        elementDetails,
      );

      if (!(displayCallout || displayAnnotation)) {
        showCallout = false;
      }
      dispatch(
        updateSideBySideElementCacheByCallouts({
          elementIds: [elementId],
          showCallout,
          isLeftSideView: isLeftView,
        }),
      );
    }
    if (showCallout && !isSideBySideView) {
      dispatch(handleElementFormulaHighlight(elementDetails, elementRef));
    } else if (!showCallout && !isSideBySideView) {
      dispatch(removeElementFormulaHighlight(elementDetails));
    }
  };

  const isFeatureFlagEnabledForComfortAssigned = dispatch(
    checkIfFeatureFlagEnabled({
      featureName: FEATURE_NAMES.COMFORT_ASSIGNED_FEATURE,
    }),
  );
  const showComfortAssignedStatus =
    showElementStatusAndFlag &&
    isFeatureFlagEnabledForComfortAssigned &&
    elementDetails &&
    elementDetails.isComfortAssign();

  const getElementCSSFromSelectedElementMap = () => {
    switch (true) {
      case elementHighlightState() === ELEMENT_HIGHLIGHT_STATES.POST_BATCH:
        return `${TIEOUT_ELEMENT}_added`;
      case elementHighlightState() ===
        ELEMENT_HIGHLIGHT_STATES.ELEMENT_MAP_SUCCESS:
        return `${TIEOUT_ELEMENT}_side-by-side-map-success`;
      case elementHighlightState() ===
        ELEMENT_HIGHLIGHT_STATES.ELEMENT_MAP_FAILURE:
        return `${TIEOUT_ELEMENT}_side-by-side-map-failure`;
      case elementHighlightState() ===
        ELEMENT_HIGHLIGHT_STATES.CALLOUT_DRAG_FAILURE:
        return `${TIEOUT_ELEMENT}__callout-update-failure`;
      case elementHighlightState() ===
        ELEMENT_HIGHLIGHT_STATES.HOVERED_FROM_STATEMENT_CONTENT:
        return `${TIEOUT_ELEMENT}__hovered-from-statement-content`;
      default:
        return '';
    }
  };

  const createNewTickmarkHandler = useCallback(() => {
    setShowTickmarkModal(true);
  }, []);

  const createNewWorkPaperHandler = useCallback(() => {
    setShowWorkPaperModal(true);
  }, []);

  const onConfirmReviewClick = useCallback(() => {
    dispatch(
      handleElementQuickAction({
        element: elementDetails,
        action: { type: ELEMENT_QUICK_ACTIONS.REVIEW },
      }),
    );
    setShowReviewConfirmationModal(false);
  }, [elementDetails]);

  const onOpenReviewConfirmationModal = useCallback(() => {
    // Get fresh state from Redux store as it is a single flow and the element history might have been updated after the component is rendered
    const { data } = store.getState();
    const latestElementHistory = data?.elementPanel?.elementHistory;

    const isVerifiedByCurrentUser = latestElementHistory?.entries?.some(
      (entry) =>
        entry.elementAction === VERIFIED_STATUS &&
        entry.changedBy === currentUser?.id,
    );

    if (!elementDetails.isReviewed() && isVerifiedByCurrentUser) {
      setShowReviewConfirmationModal(true);
    } else {
      onConfirmReviewClick();
    }
  }, [elementDetails, onConfirmReviewClick, currentUser]);

  const onManageAttributesHandler = useCallback((event) => {
    // Close all other attribute menus before opening this one
    window.dispatchEvent(
      new CustomEvent('closeAllAttributeMenus', {
        detail: { id: menuIdRef.current },
      }),
    );

    // Capture position from the actual menu item click event
    if (event && event.clientX !== undefined && event.clientY !== undefined) {
      setClickPosition({ x: event.clientX, y: event.clientY });
    } else if (contextMenuPositionRef.current) {
      // Fallback to context menu position if event is not available
      setClickPosition(contextMenuPositionRef.current);
    }
    setShowManageAttributesMenu(true);
  }, []);

  const getTickmarkHandlers = () => {
    return createConfirmOverrideSystemFlagHandlers(
      overrideSystemFlagElement,
      dispatch,
    );
  };

  const handleUpdateOverrideSystemFlag = async ({ systemFlag }) => {
    if (!overrideSystemFlagElement) {
      throw new Error(
        'Cannot update override system flag: no element selected',
      );
    }

    try {
      dispatch(
        handleSystemFlagOverride({
          element: overrideSystemFlagElement,
          systemFlag,
          revisionId: data.revision.id,
        }),
      );
    } finally {
      setShowConfirmOverrideSystemFlag(false);
      setOverrideSystemFlagElement(null);
    }
  };

  const createOverrideSystemFlagHandler = (element) => {
    if (element.isSystemOverrideFlagged()) {
      dispatch(
        handleElementQuickAction({
          element,
          action: { type: ELEMENT_QUICK_ACTIONS.OVERRIDE_FLAG },
        }),
      );
    } else {
      setOverrideSystemFlagElement(element);
      setShowConfirmOverrideSystemFlag(true);
    }
    setContextMenu(null);
  };

  const updateTickmarkSubMenu = (tickmarkMenuList, submenuList) => {
    const tickmarkMenuListClone = cloneDeep(tickmarkMenuList);
    tickmarkMenuListClone.forEach((menuItem) => {
      if (menuItem.type === ELEMENT_QUICK_ACTIONS.ADD_TICKMARK) {
        const subMenu = menuItem.children.map((subMenuItem) => {
          if (
            subMenuItem.type === ELEMENT_QUICK_ACTIONS.ATTACH_EXISTING_TICKMARK
          ) {
            return {
              ...subMenuItem,
              children: [...submenuList],
            };
          }
          return subMenuItem;
        });
        menuItem.children = subMenu;
      }
    });
    return tickmarkMenuListClone;
  };

  const updateWorkpaperSubMenu = (workpaperMenuList, submenuList) => {
    const workpaperMenuListClone = cloneDeep(workpaperMenuList);
    workpaperMenuListClone.forEach((menuItem) => {
      if (menuItem.type === ELEMENT_QUICK_ACTIONS.ADD_WORKPAPER) {
        const subMenu = menuItem.children.map((subMenuItem) => {
          if (
            subMenuItem.type === ELEMENT_QUICK_ACTIONS.ATTACH_EXISTING_WORKPAPER
          ) {
            return {
              ...subMenuItem,
              children: [...submenuList],
            };
          }
          return subMenuItem;
        });
        menuItem.children = subMenu;
      }
    });
    return workpaperMenuListClone;
  };

  const onAttachExistingTickmarks = async (element) => {
    setMenuOptions((state) => updateTickmarkSubMenu(state, [LOADER]));
    const response = await getTickmarksForRevisionRequest({
      revisionId: element.revisionId,
    });
    const tickmarkList = removeObjectsByOrdinal(
      response?.data?.result || [],
      element.tickmarks,
    );

    setExistingTickmarkList(tickmarkList);
    if (tickmarkList.length === 0) {
      setMenuOptions((state) =>
        updateTickmarkSubMenu(state, [NO_TICKMARK_OPTION]),
      );
      return;
    }
    const sortedTickmarks = tickmarkList.sort(
      (a, b) => b.elementCount - a.elementCount,
    );
    let tickmarksMenuList = [];

    if (sortedTickmarks.length > 3) {
      const top3Tickmarks = sortedTickmarks.slice(0, 3);
      tickmarksMenuList = top3Tickmarks.map((tickmark) => ({
        ...tickmark,
        id: `${tickmark.tickmarkId}-${tickmark.noteOrdinal}`,
        type: ELEMENT_QUICK_ACTIONS.TICKMARK_LIST_ITEM,
        label: `{${tickmark.noteOrdinal}}...`,
        disabled: false,
        shouldCloseMenuOnClick: true,
        shouldDispatch: false,
        isNotInternationalized: true,
        tooltipConfig: {
          id: `${tickmark.tickmarkId}-${tickmark.noteOrdinal}`,
          text: tickmark.text,
          isNotInternationalized: true,
        },
      }));
      tickmarksMenuList.push(ELLIPSIS_OPTION);
    } else {
      tickmarksMenuList = sortedTickmarks.map((tickmark) => ({
        ...tickmark,
        id: `${tickmark.tickmarkId}-${tickmark.noteOrdinal}`,
        type: ELEMENT_QUICK_ACTIONS.TICKMARK_LIST_ITEM,
        label: `{${tickmark.noteOrdinal}}...`,
        disabled: false,
        shouldCloseMenuOnClick: true,
        shouldDispatch: false,
        isNotInternationalized: true,
        tooltipConfig: {
          id: `${tickmark.tickmarkId}-${tickmark.noteOrdinal}`,
          text: tickmark.text,
          isNotInternationalized: true,
        },
      }));
    }
    setMenuOptions((state) => updateTickmarkSubMenu(state, tickmarksMenuList));
  };

  const onAttachExistingWorkpapers = async (element) => {
    try {
      setMenuOptions((state) => updateWorkpaperSubMenu(state, [LOADER]));

      const allWorkpapersResponse = await getWorkpapersForRevisionRequest({
        revisionId: element.revisionId,
      });

      const allWorkpapers = allWorkpapersResponse?.data?.result || [];
      const elementWorkpaperRefs = element.workpaperReferenceNumberList || [];
      const attachedReferenceNumbers = new Set(
        elementWorkpaperRefs.map((ref) => ref.split('|')[0]),
      );
      const availableWorkpapers = allWorkpapers.filter(
        (wp) =>
          !attachedReferenceNumbers.has(wp.referenceNumber) &&
          wp.workpaperType !== AUTO_TIEOUT_WORKPAPER_TYPE &&
          wp.workpaperType !== AUTO_TIEOUT_TB_WORKPAPER_TYPE &&
          wp.workpaperType !== WORKPAPER_TYPE.OMNIA_TRIAL_BALANCE,
      );

      setExistingWorkpaperList(availableWorkpapers);
      setAttachedWorkpaperIds([]);

      if (availableWorkpapers.length === 0) {
        setMenuOptions((state) =>
          updateWorkpaperSubMenu(state, [NO_WORKPAPER_OPTION]),
        );
        return;
      }

      const sortedWorkpapers = availableWorkpapers.sort(
        (a, b) => b.elementCount - a.elementCount,
      );
      let workpapersMenuList = [];

      if (sortedWorkpapers.length > 3) {
        const top3Workpapers = sortedWorkpapers.slice(0, 3);
        workpapersMenuList = top3Workpapers.map((workpaper) => {
          const hasReferenceName =
            workpaper.referenceName && workpaper.referenceName.trim() !== '';

          return {
            ...workpaper,
            id: `${workpaper.workpaperRefId}`,
            type: ELEMENT_QUICK_ACTIONS.WORKPAPER_LIST_ITEM,
            label: hasReferenceName
              ? `${workpaper.referenceNumber}...`
              : workpaper.referenceNumber,
            shouldCloseMenuOnClick: true,
            shouldDispatch: false,
            isNotInternationalized: true,
            tooltipConfig: hasReferenceName
              ? {
                  id: `workpaper-ref-name-${workpaper.workpaperRefId}`,
                  text: workpaper.referenceName,
                  isNotInternationalized: true,
                }
              : null,
          };
        });
        workpapersMenuList.push({
          ...ELLIPSIS_OPTION,
          parentType: ELEMENT_QUICK_ACTIONS.ADD_WORKPAPER,
        });
      } else {
        workpapersMenuList = sortedWorkpapers.map((workpaper) => {
          const hasReferenceName =
            workpaper.referenceName && workpaper.referenceName.trim() !== '';

          return {
            ...workpaper,
            id: `${workpaper.workpaperRefId}`,
            type: ELEMENT_QUICK_ACTIONS.WORKPAPER_LIST_ITEM,
            label: hasReferenceName
              ? `${workpaper.referenceNumber}...`
              : workpaper.referenceNumber,
            shouldCloseMenuOnClick: true,
            shouldDispatch: false,
            isNotInternationalized: true,
            tooltipConfig: hasReferenceName
              ? {
                  id: `workpaper-ref-name-${workpaper.workpaperRefId}`,
                  text: workpaper.referenceName,
                  isNotInternationalized: true,
                }
              : null,
          };
        });

        // No ellipsis needed when 3 or fewer workpapers
      }
      setMenuOptions((state) =>
        updateWorkpaperSubMenu(state, workpapersMenuList),
      );
    } catch (error) {
      console.error('Error fetching workpapers:', error);
      setMenuOptions((state) =>
        updateWorkpaperSubMenu(state, [NO_WORKPAPER_OPTION]),
      );
    }
  };

  const onOpenExistingTickmarksModal = () => {
    setShowTickmarkListModal(true);
  };

  const onOpenExistingWorkpapersModal = () => {
    setShowWorkpaperListModal(true);
  };

  const onTickmarkListItemClick = (element, action) => {
    dispatch(
      attachTickmarkToElement({ tickmark: action, elementDetails: element }),
    );
  };

  const onWorkpaperListItemClick = async (element, action) => {
    try {
      const workpaperModel = new WorkpaperForm({
        data: {
          revisionId: action.revisionId,
          elementId: element.id,
          referenceNumber: action.referenceNumber,
          referenceName: action.referenceName,
          workpaperRefId: action.workpaperRefId,
          workpaperRefCreatedBy: action.workpaperRefCreatedBy,
          workpaperRefCreatedDate: action.workpaperRefCreatedDate,
          creationType: action.creationType,
        },
      });

      dispatch(updateWorkpaper({ workpaper: workpaperModel }));
      dispatch(
        updateElementCacheByCalloutAction({
          elementIds: [element.id],
          showCallout: true,
        }),
      );
    } catch (error) {
      console.error('Error attaching workpaper:', error);
    }
  };

  const handleEllipsisOptionClick = (element, action) => {
    const parentType = action.parentType;
    if (parentType === ELEMENT_QUICK_ACTIONS.ADD_WORKPAPER) {
      onOpenExistingWorkpapersModal(element);
    } else {
      onOpenExistingTickmarksModal(element);
    }
  };

  const handleQuickActionMenuClick = useCallback(
    ({ element, action, event }) => {
      const actionToHandlerMap = {
        [ELEMENT_QUICK_ACTIONS.CREATE_NEW_TICKMARK]: createNewTickmarkHandler,
        [ELEMENT_QUICK_ACTIONS.OVERRIDE_FLAG]: () =>
          createOverrideSystemFlagHandler(element),
        [ELEMENT_QUICK_ACTIONS.CREATE_NEW_WORKPAPER]: createNewWorkPaperHandler,
        [ELEMENT_QUICK_ACTIONS.ATTACH_EXISTING_TICKMARK]:
          onAttachExistingTickmarks,
        [ELEMENT_QUICK_ACTIONS.ATTACH_EXISTING_WORKPAPER]:
          onAttachExistingWorkpapers,
        [ELEMENT_QUICK_ACTIONS.ELLIPSIS_OPTION]: handleEllipsisOptionClick,
        [ELEMENT_QUICK_ACTIONS.TICKMARK_LIST_ITEM]: onTickmarkListItemClick,
        [ELEMENT_QUICK_ACTIONS.WORKPAPER_LIST_ITEM]: onWorkpaperListItemClick,
        [ELEMENT_QUICK_ACTIONS.MANAGE_ATTRIBUTES]: () =>
          onManageAttributesHandler(event),
        [ELEMENT_QUICK_ACTIONS.REVIEW]: onOpenReviewConfirmationModal,
      };

      actionToHandlerMap[action.type]?.(element, action);
    },
    [
      createNewTickmarkHandler,
      createNewWorkPaperHandler,
      onManageAttributesHandler,
      onAttachExistingTickmarks,
      onAttachExistingWorkpapers,
      handleEllipsisOptionClick,
      onTickmarkListItemClick,
      onWorkpaperListItemClick,
      onOpenReviewConfirmationModal,
    ],
  );

  const closingAttributeMenuWindow = useCallback(() => {
    // Close all attribute menus across all elements
    window.dispatchEvent(
      new CustomEvent('closeAllAttributeMenus', {
        detail: { id: contextMenuIdRef.current },
      }),
    );
    setShowManageAttributesMenu(false);
    setClickPosition(null);
  }, []);

  const handleContextMenu = useCallback(
    (event) => {
      closingAttributeMenuWindow();
      const shouldShowContextMenu = calculateContextMenuVisibility({
        selectMode,
        workFlowStatus: effectiveWorkFlowStatus,
        isSideBySideView,
        isAutoTieoutMode,
        isTrialBalanceMode,
        isLeftView,
        batchElementList,
        elementId: elementDetails.id,
      });
      if (
        !shouldShowContextMenu ||
        selectMode === ELEMENT_SELECT_MODES.FORMULA
      ) {
        return;
      }

      let mappedElements = {};
      if (isAutoTieoutMode) mappedElements = autoTieoutElementData;
      else if (isTrialBalanceMode) mappedElements = trialBalanceMappedElements;

      const menuOptionsByElement = getQuickActionByElement(
        elementDetails,
        isStatementWrappedOrPendingApproval,
        mappedElements,
        isCftEnabled,
      );
      if (
        isReadOnlyStatement ||
        (!menuOptionsByElement &&
          Array.isArray(menuOptionsByElement) &&
          menuOptionsByElement.length === 0)
      ) {
        return;
      }

      setMenuOptions(menuOptionsByElement);

      if (elementDetails.isVerified()) {
        dispatch(getHistoryForElementPanel(elementDetails.id));
      }

      event.preventDefault();
      event.stopPropagation();

      // Capture the click position for the manage attributes menu
      contextMenuPositionRef.current = {
        x: event.clientX,
        y: event.clientY,
      };

      // using element ID from the DOM
      const clickedElement = event.currentTarget;
      const elementIdFromDom = clickedElement.getAttribute('id');
      const elementIdNumericPart = elementIdFromDom.replace(
        TIEOUT_ELEMENT_ID_PREFIX,
        '',
      );
      // Set selected element ID state
      setSelectedElementId(parseInt(elementIdNumericPart));

      // unique ID for context menu on each instance
      const menuId = `context-menu-${Date.now()}-${Math.random()}`;
      contextMenuIdRef.current = menuId;

      // Dispatch event to close other context menus if any other are open
      window.dispatchEvent(
        new CustomEvent('closeOtherContextMenus', { detail: { id: menuId } }),
      );

      // just for understanding
      // Using polygonInPixel data to get exact coordinates
      // polygonInPixel can be:
      // - Single-line: [x1, y1, x2, y2, x3, y3, x4, y4]
      // - Multi-line: [[x1, y1, x2, y2, x3, y3, x4, y4], [x1, y1, x2, y2, x3, y3, x4, y4], ...]
      const isMultiLine = Array.isArray(polygonInPixel[0]);

      // For multiline, use the last line's coordinates to position at bottom right
      const polygonCoords = isMultiLine
        ? polygonInPixel[polygonInPixel.length - 1] // Last line for multiline
        : polygonInPixel; // Single line

      // Extracting coordinates from polygon array
      const x1 = polygonCoords[0]; // Top-left X coordinate
      const y1 = polygonCoords[1]; // Top-left Y coordinate
      const x2 = polygonCoords[2]; // Top-right X coordinate
      const y2 = polygonCoords[3]; // Top-right Y coordinate
      const x3 = polygonCoords[4]; // Bottom-right X coordinate
      const y3 = polygonCoords[5]; // Bottom-right Y coordinate
      const x4 = polygonCoords[6]; // Bottom-left X coordinate
      const y4 = polygonCoords[7]; // Bottom-left Y coordinate

      const targetRect = {
        top: polygonTop,
        right: x3, // Bottom-right x coordinate
        bottom: y3, // Bottom-right y coordinate
        left: polygonLeft,
        width: polygonWidth,
        height: polygonHeight,
      };

      setContextMenu({
        x: x3, // Position at bottom-right X
        y: y3, // Position at bottom-right Y
        elementId: elementIdFromDom,
        targetRect: targetRect,
        // Position data from OCR polygon coordinates
        positionData: {
          polygonInPixel: polygonInPixel, // Complete polygon data (single or multiline)
          isMultiLine: isMultiLine,
          coordinates: {
            top: polygonTop,
            left: polygonLeft,
            width: polygonWidth,
            height: polygonHeight,
            // Raw polygon coordinates for last line/segment (bottom-right positioning)
            x1,
            y1,
            x2,
            y2,
            x3,
            y3,
            x4,
            y4,
          },
        },
      });
    },
    [elementDetails, effectiveWorkFlowStatus, selectMode],
  );

  const renderElements = () => {
    const isAutoTieoutUnlinkIconVisible =
      !isLeftView &&
      ((isAutoTieoutMode &&
        workFlowStatus.isCompleted &&
        autoTieoutElementToHighlight?.[
          isLeftView ? 'sourceElementId' : 'targetElementId'
        ] == elementDetails.id) ||
        (isTrialBalanceMode &&
          tbWorkFlowStatus?.isCompleted &&
          trialBalanceElementToHighlight?.elementId == elementDetails.id));
    return (
      <>
        {!isNullOrUndefined(_countForSelectedElementForSideBySideMapping) ? (
          <div style={{ ...styles, position: 'absolute' }}>
            <div
              ref={elementRef}
              className={classNames({
                [`${SIDE_BY_SIDE_STATEMENT_ELEMENT}`]:
                  isElementSelectedForSideBySideMap(elementDetails.id),
                [`${TIEOUT_ELEMENT}--cursor-alias`]: isCursorAlias,
              })}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              onClick={(e) => {
                if (isAutoTieoutMode && workFlowStatus.isCompleted) {
                  handleAutoTieoutElementClick({ elementDetails });
                }
                if (
                  isTrialBalanceMode &&
                  !isLeftView &&
                  tbWorkFlowStatus?.isCompleted
                ) {
                  handleTrialBalanceElementClick({ elementDetails });
                  return;
                }
                onClick(e, elementDetails);
              }}
              onContextMenu={
                editPermissionAutotieout ? handleContextMenu : undefined
              }
              onMouseEnter={() => {
                handleIndicatorOnHover(elementDetails.id, true);
              }}
              onMouseLeave={() => {
                handleIndicatorOnHover(elementDetails.id, false);
              }}
            ></div>
            <span
              className={classNames([
                `${SIDE_BY_SIDE_ELEMENT_IDENTIFIER}__count`,
              ])}
            >
              {showAnnotationIcons &&
                _countForSelectedElementForSideBySideMapping}
            </span>
          </div>
        ) : (
          <div
            ref={elementRef}
            id={`${TIEOUT_ELEMENT_ID_PREFIX}${elementDetails.id}`}
            style={{ ...styles, position: 'absolute', zIndex: 48 }}
            className={classNames(
              `${TIEOUT_ELEMENT_CLASS}-${elementDetails.id}`,
              {
                [`${TIEOUT_ELEMENT_CLASS}`]: true,
                [`${TIEOUT_ELEMENT_CLASS}--disabled`]:
                  (isDisabled || handleCursorByStmtWrappedUpState()) &&
                  !(
                    !isLeftView &&
                    (isAutoTieoutMode || isTrialBalanceMode) &&
                    isModeForAddCursor(selectMode)
                  ),
                [`${TIEOUT_ELEMENT_CLASS}__added`]:
                  elementHighlightState() ===
                  ELEMENT_HIGHLIGHT_STATES.POST_BATCH,
                [`${TIEOUT_ELEMENT}--cursor-alias`]: isCursorAlias,
                [getElementCSSFromSelectedElementMap()]: true,
                [`${TIEOUT_ELEMENT_CLASS}__verified`]: isVerified,
                [`${TIEOUT_ELEMENT_CLASS}__reviewed`]: isReviewed,
                [`${TIEOUT_ELEMENT_CLASS}__unverified`]: isUnverified,
                [`${TIEOUT_ELEMENT_CLASS}__mapped-element-highlight`]:
                  shouldHighlightMappedElements(),
                [`${TIEOUT_ELEMENT_CLASS}--selected--${modifier}`]:
                  (isTrialBalanceMode ||
                    isAutoTieoutMode ||
                    !isSideBySideView) &&
                  isSelected,
                [`${TIEOUT_ELEMENT_CLASS}--highlighted`]: isHighlighted,
                [`${TIEOUT_ELEMENT_CLASS}--auto-tieout-element-highlight`]:
                  autoTieoutElementToHighlight?.[
                    isLeftView ? 'sourceElementId' : 'targetElementId'
                  ] == elementDetails.id ||
                  (isTrialBalanceMode &&
                    trialBalanceElementToHighlight?.elementId ==
                      elementDetails.id),
                [`${TIEOUT_ELEMENT}--cursor-disabled`]:
                  isSideBySideViewDisabled(elementDetails.id),
                [`${TIEOUT_ELEMENT_CLASS}--comfort-assigned`]:
                  showComfortAssignedStatus,
              },
            )}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              if (
                isAutoTieoutMode &&
                workFlowStatus.isCompleted &&
                !isModeForAddCursor(selectMode)
              ) {
                handleAutoTieoutElementClick({ elementDetails });
              }
              if (
                isTrialBalanceMode &&
                !isLeftView &&
                tbWorkFlowStatus?.isCompleted &&
                !isModeForAddCursor(selectMode)
              ) {
                handleTrialBalanceElementClick({ elementDetails });
                return;
              }
              onClick(e, elementDetails);
            }}
            onContextMenu={
              editPermissionAutotieout ? handleContextMenu : undefined
            }
            onMouseEnter={() => {
              handleIndicatorOnHover(elementDetails.id, true);
              dispatch(handleElementFormulaHighlight(elementDetails));
              handleMouseEnter();
            }}
            onMouseLeave={() => {
              if (
                !annotationDisplayFilter.expandAllElements ||
                !isElementSelected
              ) {
                handleIndicatorOnHover(elementDetails.id, false);
              }
              dispatch(removeElementFormulaHighlight(elementDetails));
              handleMouseLeave();
            }}
          ></div>
        )}
        {isAutoTieoutUnlinkIconVisible &&
          handleCrossIconByStmtWrappedUpState() &&
          showAnnotationIcons && (
            <div style={{ ...styles, position: 'absolute' }}>
              <Tooltip
                id={`${SIDE_BY_SIDE_ELEMENT_IDENTIFIER}__detach-wp-tooltip`}
                text={
                  isTrialBalanceMode
                    ? 'trial-balance-view.auto-tieout.wp-remove-reference.tooltip'
                    : 'common.unlink'
                }
              >
                <CrossIcon
                  width={CROSS_ICON_SIZE}
                  height={CROSS_ICON_SIZE}
                  className={`${TIEOUT_ELEMENT_CLASS}__cross-icon`}
                  style={{ left: `${polygonWidth - 10}px` }}
                  onClick={() => {
                    dispatch(
                      unlinkElementWorkpaperFromAutoTieout({
                        workpaper: workpaperDetails,
                        elementDetails,
                      }),
                    );
                    dispatch(clearSideBySideElementsMap());
                  }}
                />
              </Tooltip>
            </div>
          )}
        {showAnnotationIcons && (
          <div style={{ ...styles, position: 'absolute' }}>
            {trialBalanceWPIcon({
              viewMode,
              elementData: elementDetails,
              BLOCK: TIEOUT_ELEMENT_CLASS,
              style: {
                left: `${polygonWidth + 3}px`,
              },
            })}
          </div>
        )}
      </>
    );
  };

  const closeContextMenu = useCallback(
    (data, item, event) => {
      const { type, shouldCloseMenuOnClick, shouldDispatch } = item || {};

      if (
        type !== ELEMENT_QUICK_ACTIONS.LOADER &&
        !isNullOrUndefined(item) &&
        !isNullOrUndefined(data)
      ) {
        if (shouldDispatch) {
          dispatch(handleElementQuickAction({ element: data, action: item }));
        } else {
          handleQuickActionMenuClick({ element: data, action: item, event });
        }
      }

      if (!item || shouldCloseMenuOnClick) {
        setContextMenu(null);
      }

      if (type === ELEMENT_QUICK_ACTIONS.CREATE_NEW_WORKPAPER) {
        setShowWorkPaperModal(true);
      }
    },
    [handleQuickActionMenuClick],
  );

  return (
    <>
      {polygonInPixel && polygonInPixel.length ? (
        <>
          {showElementStatusAndFlag &&
            displayElementDetailsOnStatementPage &&
            showAnnotationIcons && (
              <ElementFlagComponentOCR
                elementDetails={elementDetails}
                polygonTop={polygonTop}
                polygonLeft={polygonLeft}
              />
            )}
          {renderElements()}
          {contextMenu && (
            <QuickActionElementContextMenu
              data={elementDetails}
              menuOptions={menuOptions}
              position={contextMenu}
              onClose={closeContextMenu}
              isOCR={true}
              containerId={
                !isLeftView && isSideBySideView
                  ? SIDE_BY_SIDE_VIEW_RIGHT_BLOCK
                  : undefined
              }
            />
          )}
          <CreateTickmark
            show={showTickmarkModal}
            setShow={setShowTickmarkModal}
            elementDetails={elementDetails}
          />
          {showManageAttributesMenu && (
            <ManageElementAttributesMenu
              show={showManageAttributesMenu}
              setShow={setShowManageAttributesMenu}
              elementDetails={elementDetails}
              clickPosition={clickPosition}
            />
          )}
          {showConfirmOverrideSystemFlag && overrideSystemFlagElement && (
            <ConfirmOverrideSystemFlag
              onClickCancel={() => {
                setShowConfirmOverrideSystemFlag(false);
                setOverrideSystemFlagElement(null);
              }}
              updateOverrideSystemFlag={handleUpdateOverrideSystemFlag}
              {...getTickmarkHandlers()}
              disabled={false}
            />
          )}
          <CreateWorkPaper
            show={showWorkPaperModal}
            setShow={setShowWorkPaperModal}
            elementDetails={elementDetails}
          />
          {showTickmarkListModal && (
            <AttachExistingTickmarks
              onClose={() => {
                setSelectedElementId(null);
                setShowTickmarkListModal(false);
              }}
              listData={existingTickmarkList}
              elementIds={[selectedElementId]}
              revisionId={revisionId}
            />
          )}
          {showWorkpaperListModal && (
            <AttachExistingWorkpapers
              onClose={() => {
                setSelectedElementId(null);
                setShowWorkpaperListModal(false);
              }}
              listData={existingWorkpaperList}
              elementDetails={elementDetails}
              revisionId={revisionId}
              alreadyAddedWorkpaperIds={attachedWorkpaperIds}
            />
          )}
          {showReviewConfirmationModal && (
            <ConfirmationModal
              title={'element-panel.checkbox.review.confirmation.modal.title'}
              textId={'element-panel.checkbox.review.confirmation.modal.text'}
              onClose={() => setShowReviewConfirmationModal(false)}
              onConfirm={onConfirmReviewClick}
              primaryButtonText={'common.yes'}
              secondaryButtonText={'common.no'}
            />
          )}
        </>
      ) : null}
    </>
  );
};
export default TieoutElementOCR;

TieoutElementOCR.propTypes = {
  /** func to clear text selection */
  clearSelection: PropTypes.func.isRequired,
  /** Element details object */
  elementDetails: PropTypes.object.isRequired,
  /** Array containing boundary information */
  polygonInPixel: PropTypes.array,
  /** Boolean to decide the visibility of element status */
  showElementStatusAndFlag: PropTypes.bool,
  /** Object containing the filter config from summary bar */
  annotationDisplayFilter: PropTypes.object.isRequired,
  /** Object having properties fordisplaying marker or callout */
  annotation: PropTypes.object.isRequired,
  /** Booloean to check left or right view */
  isLeftView: PropTypes.bool,
  /** Boolean to disable element selection */
  isDisabled: PropTypes.bool,
  /** show annotation icons */
  showAnnotationIcons: PropTypes.bool,
  /** Boolean to highlight mapped elements */
  highlightMappedElements: PropTypes.bool,
};
